import * as Yup from 'yup';
import dayjs from 'dayjs';
import piiFormModel from './formModel';

const {
  nameModel,
  addressModel,
  birthDateModel,
  phoneNumberModel,
  ssnModel,
  emailAddressModel,
  usernameModel,
} = piiFormModel;

const {
  formFields: { first, middle, last, suffix },
} = nameModel;

const {
  formFields: { street1, street2, city, state, zip, country },
} = addressModel;

const {
  formFields: { birthDate },
} = birthDateModel;

const {
  formFields: { phoneNumber },
} = phoneNumberModel;

const {
  formFields: { ssn, confirmSsn },
} = ssnModel;

const {
  formFields: { emailAddress, confirmEmail },
} = emailAddressModel;

const {
  formFields: { username },
} = usernameModel;

export const firstNameValidation = Yup.string()
  .min(first.min, first.minErrMsg)
  .max(first.max, first.maxErrMsg)
  .matches(first.matches, first.matchesErrMsg)
  .required(first.requiredErrMsg);

export const middleNameValidation = Yup.string()
  .nullable()
  .max(middle.max, middle.maxErrMsg)
  .matches(middle.matches, {
    message: middle.matchesErrMsg,
    excludeEmptyString: true,
  });

export const lastNameValidation = Yup.string()
  .min(last.min, last.minErrMsg)
  .max(last.max, last.maxErrMsg)
  .matches(last.matches, last.matchesErrMsg)
  .required(last.requiredErrMsg);

/* Nullable because the field seeds `null` so its placeholder shows rather than the "None"
   option (see `Name/FormModel/useNameInitialValues`); an untouched Suffix stays valid. */
export const suffixValidation = Yup.string()
  .nullable()
  .oneOf([...suffix.oneOf, null], suffix.oneOfErrMsg);

export const street1Validation = Yup.string()
  .min(street1.min, street1.minErrMsg)
  .max(street1.max, street1.maxErrMsg)
  .matches(street1.matches, street1.matchesErrMsg)
  .required(street1.requiredErrMsg);

export const street2Validation = Yup.string()
  .min(street2.min, street2.minErrMsg)
  .max(street2.max, street2.maxErrMsg)
  .matches(street2.matches, street2.matchesErrMsg)
  .notRequired();

export const cityValidaton = Yup.string()
  .matches(city.matches, city.matchesErrMsg)
  .test('no-hyphen-start-end', city.nonMatchesErrMsg, (value) => {
    return !value || (!value.startsWith('-') && !value.endsWith('-'));
  })
  .min(city.min, city.minErrMsg)
  .max(city.max, city.maxErrMsg)
  .required(city.requiredErrMsg);

export const stateValidation = Yup.string().required(state.requiredErrMsg);

export const zipValidation = Yup.string()
  .matches(zip.matches, zip.matchesErrMsg)
  .length(zip.length, zip.lengthErrMsg)
  .required(zip.requiredErrMsg);

export const countryValidation = Yup.string().required(country.requiredErrMsg);

/*
 * Two input shapes reach this validator:
 *   • `DatePicker` writes an ISO `YYYY-MM-DD` string via
 *     `dayjs.format('YYYY-MM-DD')` — dayjs parses this natively.
 *   • `InputMask mask='00/00/0000'` writes the UNMASKED digits
 *     (e.g. `12251990` for 12/25/1990). dayjs's default parser does
 *     not handle this shape — depending on the JS engine it either
 *     returns an invalid date OR silently parses it as year 1225
 *     (lenient legacy `new Date` behaviour), which then trips the
 *     `max-age` "more than 120 years ago" test on every valid date.
 *     Guard by regex-parsing the digits into a real `Date` first.
 *
 * A test that has nothing to judge (empty or unparseable) must pass so
 * each rule only reports its own failure. `required` covers empty.
 */
const MMDDYYYY_DIGITS_RE = /^(\d{2})(\d{2})(\d{4})$/;

function parseBirthDateValue(value?: string): Date | null {
  if (!value) return null;
  const match = MMDDYYYY_DIGITS_RE.exec(value);
  if (match) {
    const month = Number(match[1]);
    const day = Number(match[2]);
    const year = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1) return null;
    const d = new Date(year, month - 1, day);
    if (
      d.getFullYear() !== year ||
      d.getMonth() !== month - 1 ||
      d.getDate() !== day
    ) {
      return null;
    }
    return d;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : null;
}

const isSkippable = (value?: string) => parseBirthDateValue(value) === null;

export const birthDateValidation = Yup.string()
  .required(birthDate.requiredErrMsg)
  .test(
    'valid-date',
    birthDate.validErrMsg,
    (value) => !value || parseBirthDateValue(value) !== null,
  )
  .test('not-in-future', birthDate.futureErrMsg, (value) => {
    if (isSkippable(value)) return true;
    return !dayjs(parseBirthDateValue(value) as Date).isAfter(dayjs(), 'day');
  })
  .test('min-age', birthDate.minAgeErrMsg, (value) => {
    if (isSkippable(value)) return true;
    return !dayjs(parseBirthDateValue(value) as Date).isAfter(
      dayjs().subtract(birthDate.minAge, 'year'),
      'day',
    );
  })
  .test('max-age', birthDate.maxAgeErrMsg, (value) => {
    if (isSkippable(value)) return true;
    return !dayjs(parseBirthDateValue(value) as Date).isBefore(
      dayjs().subtract(birthDate.maxAge, 'year'),
      'day',
    );
  });

// Family-plan Add Child form. Reuses the shared PII copy so a change
// to any DOB message ripples through every consumer. The child DOB
// field is an `InputMask` with mask `00/00/0000`; InputMask writes
// the unmasked value into Formik, so the schema receives an
// `MMDDYYYY` 8-digit string (empty when nothing entered). Parsing is
// done by regex + `new Date(y, m, d)` — Date's getters catch invalid
// calendar days (Feb 30, Apr 31, …) without needing dayjs's
// customParseFormat plugin, which isn't extended app-wide.
const CHILD_DOB_DIGITS_RE = /^(\d{2})(\d{2})(\d{4})$/;

function parseChildDob(value: string): Date | null {
  const match = CHILD_DOB_DIGITS_RE.exec(value);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1) return null;
  const d = new Date(year, month - 1, day);
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
}

export const childBirthDateValidation = Yup.string()
  .required(birthDate.requiredErrMsg)
  .test('valid-date', birthDate.invalidErrMsg, (value) => {
    if (!value) return false;
    return parseChildDob(value) !== null;
  })
  .test('is-past', birthDate.pastErrMsg, (value) => {
    if (!value) return false;
    const d = parseChildDob(value);
    return d !== null && dayjs(d).isBefore(dayjs(), 'day');
  })
  .test('is-minor', birthDate.minorErrMsg, (value) => {
    if (!value) return false;
    const d = parseChildDob(value);
    return d !== null && dayjs().diff(dayjs(d), 'year') < birthDate.childMaxAge;
  });

export const phoneNumberValidation = Yup.string()
  .matches(phoneNumber.matches, phoneNumber.matchesErrMsg)
  .length(phoneNumber.length, phoneNumber.lengthErrMsg)
  .required(phoneNumber.requiredErrMsg);

export const ssnValidation = Yup.string()
  .length(9, ssn.lengthErrMsg)
  .matches(ssn.matches, ssn.matchesErrMsg)
  .required(ssn.requiredErrMsg);

export const confirmSsnValidation = Yup.string()
  .oneOf([Yup.ref('ssn')], confirmSsn.matchesErrMsg)
  .required(confirmSsn.requiredErrMsg);

export const emailAddressValidation = Yup.string()
  .email(emailAddress.emailErrMsg)
  .max(emailAddress.max, emailAddress.maxErrMsg)
  .matches(emailAddress.matches, emailAddress.matchesErrMsg)
  .required(emailAddress.requiredErrMsg);

/**
 * Confirm-email counterpart. Mirrors Angular's `related-adult.ts:22-26`
 * — only `Required` + `MustMatch`, no email/pattern check (the confirm
 * value only has to equal the already-validated primary field). Consumers
 * pass their primary email field name in — default `'emailAddress'`.
 */
export const confirmEmailAddressValidation = (
  emailFieldName = 'emailAddress',
) =>
  Yup.string()
    .required(confirmEmail.requiredErrMsg)
    .oneOf([Yup.ref(emailFieldName)], confirmEmail.matchesErrMsg);

export const usernameValidation = Yup.string()
  .min(username.min, username.minErrMsg)
  .max(username.max, username.maxErrMsg)
  .matches(username.matches, username.matchesErrMsg)
  .required(username.requiredErrMsg);
